import { useFlags } from "launchdarkly-react-client-sdk";

import { Panel } from "primereact/panel";
import { InputSwitch } from "primereact/inputswitch";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import { Skeleton } from "primereact/skeleton";
import { isEmpty } from "lodash";

import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";

import { CompanyLogoCard } from "./CompanyLogoCard";
import { ExternalAccessControlCard } from "./ExternalAccessControlCard";
import { customizationsConfig, type CustomizationItem } from "./CustomizationConfig";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomizationSectionProps {
  settings: Record<string, unknown>;
  siteSettings: Record<string, unknown>;
  updateSettings?: (field: string, value: unknown) => void;
  updateSiteSettings?: (field: string, value: unknown) => void;
  updateSettingsTextField?: (field: string) => void;
  uploadLogo?: (file: File) => Promise<void>;
  removeLogo?: () => Promise<void>;
  showToast?: (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string,
    life?: number
  ) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CustomizationSection = ({
  settings,
  siteSettings,
  updateSettings = () => {},
  updateSiteSettings = () => {},
  updateSettingsTextField = () => {},
  uploadLogo = async () => {},
  removeLogo = async () => {},
  showToast = () => {},
}: CustomizationSectionProps) => {
  const { externalAccessControlGate } = useFlags();
  const { isRestrictedBundle } = useUserContext();

  if (isEmpty(settings) && isEmpty(siteSettings)) {
    return (
      <div
        className={C(
          "flex justify-between items-center w-full py-[1.125rem] pr-8 font-semibold text-color"
        )}
      >
        <h4 className="text-lg font-semibold">Customization</h4>
        <Skeleton width="80px" height="20px" />
      </div>
    );
  }

  const handleFieldChange = (item: CustomizationItem, value: unknown) => {
    const updateFn = item.source === "siteSettings" ? updateSiteSettings : updateSettings;
    updateFn(item.key, value);
  };

  const handleTextFieldBlur = (item: CustomizationItem) => {
    updateSettingsTextField(item.key);
  };

  const renderField = (item: CustomizationItem) => {
    const source = item.source === "siteSettings" ? siteSettings : settings;
    const value = source[item.key];

    if (value === "loading") {
      return <Skeleton width="36px" height="20px" />;
    }

    switch (item.fieldType) {
      case "number":
        return (
          <InputNumber
            id={item.key}
            data-testid={`settings-${item.key}`}
            value={value as number}
            onValueChange={e => handleFieldChange(item, e.value)}
            minFractionDigits={0}
            maxFractionDigits={0}
            min={0}
            max={10}
            size={4}
          />
        );
      case "textArea":
        return (
          <InputTextarea
            id={item.key}
            data-testid={`settings-${item.key}`}
            value={value as string}
            onChange={e => handleFieldChange(item, e.target.value)}
            onBlur={() => handleTextFieldBlur(item)}
            rows={3}
            className="w-full max-w-60"
          />
        );
      case "toggle":
      default:
        return (
          <InputSwitch
            id={item.key}
            data-testid={`settings-${item.key}`}
            checked={Boolean(value)}
            onChange={e => handleFieldChange(item, e.value)}
          />
        );
    }
  };

  return (
    <Panel
      header="Customization"
      data-testid="customization-section"
      pt={{ content: { className: "bg-transparent" } }}
    >
      <div
        className="grid gap-6"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))" }}
      >
        {Object.entries(customizationsConfig).map(([sectionKey, section]) => (
          <div
            key={sectionKey}
            data-testid={`settings-section-${sectionKey}`}
            className={C(themeClasses.bgElevation2, "p-6 rounded-md shadow-md")}
          >
            <div
              className={C(
                "flex justify-between items-center border-b-2 pb-3 mb-6",
                themeClasses.borderSurfaceBorder
              )}
            >
              <h3 className="text-xl font-semibold flex-1 mb-0">{section.headerText}</h3>
            </div>
            <div className="flex flex-col gap-6">
              {section.items
                .filter(item => !item.isRestrictedSetting || !isRestrictedBundle)
                .map(item => (
                  <div key={item.key} className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <label htmlFor={item.key} className="atlas-h5 flex items-center gap-2 m-0">
                        {item.label}
                        <i
                          className={C(
                            "pi pi-info-circle text-base cursor-help",
                            themeClasses.textPrimary
                          )}
                          data-pr-tooltip={item.description}
                          data-pr-position="right"
                        />
                      </label>
                    </div>
                    <div className="flex items-center">{renderField(item)}</div>
                  </div>
                ))}
              <Tooltip target=".pi-info-circle" className="max-w-xs" />
            </div>
          </div>
        ))}
        <CompanyLogoCard
          thumbLogoUrl={siteSettings?.thumbLogoUrl as string | undefined}
          uploadLogo={uploadLogo}
          removeLogo={removeLogo}
          showToast={showToast}
        />
        {externalAccessControlGate && <ExternalAccessControlCard />}
      </div>
    </Panel>
  );
};
